import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { PageWrapper } from 'pages/_layouts/PageWrapper';
import FileViewer from 'components/file-editor-components/file-viewer';
import EditorWrapper from 'components/file-editor-components/editor-wrapper';
import LabelFields from 'components/file-editor-components/label-fields';
import TableFields from 'components/file-editor-components/table-fields';
import FieldLabelModal from 'components/file-editor-components/field-label-modal';
import { useFileEditor } from 'components/file-editor-components/useFileEditor';
import { SandboxParams } from 'entities/generative-models/types';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import {
	getGenerativeFileDetails,
	putGenerativeLabels,
	getGenerativeModelFileUrl,
} from 'entities/generative-models/api';
import { useAuthStore } from 'processes/auth/model/state';
import { useFileEditorStore } from 'store/file-editor';
import AISettings from './partials/ai-settings';
import GenerativeResearch from './partials/generative-research';

const GenerativeFileEditor = () => {
	const navigate = useNavigate();
	const { fileId } = useParams();
	const { company } = useAuthStore();
	const { setFileDetailsV2 } = useFileEditorStore();

	const [sandbox, setSandbox] = useState<SandboxParams>({
		fullModelName: '',
		reasoningEffort: '',
		extractRegions: false,
		returnDebugInfo: false,
	});

	const {
		fileDetails,
		fileUrl,
		isLoading,
		fileViewerRef,
		pendingRegion,
		regions,
		selectedSourceId,
		setSelectedSourceId,
		handleCancelLabel,
		handleSaveLabel,
		getColumnPinPreferences,
		handleTogglePinColumn,
	} = useFileEditor({
		entityId: fileId,
		detailsQueryKey: ['get/generative-file_v2', fileId],
		urlQueryKey: ['get/generative-file-url_v2', fileId],
		getDetails: getGenerativeFileDetails,
		getUrl: getGenerativeModelFileUrl,
	});

	const { mutate: putGenerativeLabelsMutate, isLoading: putIsLoading } = useMutation({
		mutationKey: ['put/generative-file-labels_v2'],
		mutationFn: putGenerativeLabels,
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	useEffect(() => {
		if (fileDetails?.sandbox) {
			setSandbox(fileDetails.sandbox);
		}
	}, [fileDetails?.sandbox]);

	const onSaveHandler = () => {
		if (fileId && fileDetails) {
			putGenerativeLabelsMutate(
				{
					generativeModelFileId: fileId,
					data: {
						labels: fileDetails.labels,
						execute: false,
						sandbox: company?.metadata?.allowGenerativeModelsSandbox ? sandbox : undefined,
					},
				},
				{
					onSuccess: () => {
						navigate(-1);
					},
					onError: (err) => {
						showErrorNotification(err as AxiosError);
					},
				},
			);
		}
	};
	const onRunHandler = () => {
		if (fileId && fileDetails) {
			putGenerativeLabelsMutate(
				{
					generativeModelFileId: fileId,
					data: {
						labels: fileDetails.labels,
						execute: true,
						sandbox: company?.metadata?.allowGenerativeModelsSandbox ? sandbox : undefined,
					},
				},
				{
					onSuccess: (response) => {
						setFileDetailsV2(response.data);
					},
					onError: (err) => {
						showErrorNotification(err as AxiosError);
					},
				},
			);
		}
	};

	return (
		<PageWrapper>
			<EditorWrapper
				onRun={onRunHandler}
				onSave={onSaveHandler}
				isSaving={putIsLoading}
				isLoading={isLoading}
				filePreview={
					fileUrl && (
						<FileViewer
							ref={fileViewerRef}
							url={fileUrl}
							fileName={fileDetails?.fileName || ''}
							regions={regions}
							drawRegions={false}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
						/>
					)
				}
				sidebar={
					<>
						<AISettings sandbox={sandbox} setSandbox={setSandbox} />
						<GenerativeResearch />
						<LabelFields
							labels={fileDetails?.recognizedLabels || []}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
						/>
						<TableFields
							labels={fileDetails?.recognizedLabels || []}
							selectedSourceId={selectedSourceId}
							onSelectSource={setSelectedSourceId}
							getColumnPinPreferences={getColumnPinPreferences}
							onTogglePinColumn={handleTogglePinColumn}
						/>
					</>
				}
			/>
			<FieldLabelModal
				fileId={fileId}
				region={pendingRegion}
				onCancel={handleCancelLabel}
				onSave={handleSaveLabel}
			/>
		</PageWrapper>
	);
};

export default GenerativeFileEditor;
